import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import InjectNotificationModal from '../components/ems/InjectNotificationModal';
import MessageNotificationPopup from '../components/ems/MessageNotificationPopup';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const socketRef = useRef(null);
  const userRef = useRef(user);

  // Notification queue for inject modal popups
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);

  // Message notification popup
  const [messagePopup, setMessagePopup] = useState(null);
  const messagePopupQueue = useRef([]);

  // Track shown event IDs to prevent duplicates
  const shownEventIdsRef = useRef(new Set());

  useEffect(() => { userRef.current = user; }, [user]);

  // Deduplicate helper — returns true if event is new
  const isNewEvent = useCallback((id) => {
    if (!id || shownEventIdsRef.current.has(id)) return false;
    shownEventIdsRef.current.add(id);
    setTimeout(() => shownEventIdsRef.current.delete(id), 300000);
    return true;
  }, []);

  // Connect to Socket.IO
  useEffect(() => {
    if (!user?.user_id) return;

    const token = localStorage.getItem('ems_token');
    if (!token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const socket = io(backendUrl, {
      path: '/api/socket.io',
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      transports: ['websocket', 'polling'],
    });

    // === LATENCY MEASUREMENT ===
    let latencyInterval = null;
    const startLatencyPings = () => {
      if (latencyInterval) clearInterval(latencyInterval);
      latencyInterval = setInterval(() => {
        socket.emit('latency_ping', { t: Date.now() });
      }, 10000);
      // Send first ping immediately
      socket.emit('latency_ping', { t: Date.now() });
    };

    socket.on('connect', () => {
      console.log('[Socket.IO] Connected:', socket.id);
      setConnected(true);
      setReconnecting(false);
      startLatencyPings();
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.IO] Disconnected:', reason);
      setConnected(false);
      if (latencyInterval) { clearInterval(latencyInterval); latencyInterval = null; }
    });

    socket.on('reconnect_attempt', (attempt) => {
      console.log('[Socket.IO] Reconnecting... attempt', attempt);
      setReconnecting(true);
    });

    socket.on('reconnect', () => {
      console.log('[Socket.IO] Reconnected');
      setReconnecting(false);
      setConnected(true);
    });

    socket.on('reconnect_failed', () => {
      console.log('[Socket.IO] Reconnection failed');
      setReconnecting(false);
    });

    // Latency pong handler
    socket.on('latency_pong', (data) => {
      if (data?.t) {
        const rtt = Date.now() - data.t;
        socket.emit('latency_report', { latency_ms: rtt });
      }
    });

    // === EVENT LISTENERS (17 events) ===

    // 1. notification — generic notification (bell icon)
    socket.on('notification', (data) => {
      const notifData = data?.data || data;
      const notifId = notifData?.notification_id;
      if (notifId && !isNewEvent(notifId)) return;

      setMessages(prev => [...prev.slice(-99), { type: 'notification', data: notifData }]);
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'notification', data: notifData } }));

      // If it's a new_message notification, show popup (skip for participants)
      if (notifData?.type === 'new_message') {
        // Show floating notification for ALL roles (participant, simcell, observer, admin)
        const popupData = {
          title: notifData.title || 'New Message',
          message: notifData.message || '',
          from_name: notifData.from_name || '',
          exercise_name: notifData.exercise_name || '',
          exercise_id: notifData.exercise_id,
          timestamp: Date.now(),
        };
        if (!messagePopup) {
          setMessagePopup(popupData);
        } else {
          messagePopupQueue.current.push(popupData);
        }
      }
    });

    // 2. new_message — direct message received (V14 BUG FIX: emit toast + dispatch event)
    socket.on('new_message', (data) => {
      const msgId = data?.message_id;
      if (msgId && !isNewEvent(`msg_${msgId}`)) return;

      // Dispatch DOM event so MessagesPanel can append in real-time without refresh
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'new_message', ...data } }));

      // V14 BUG FIX (Issue 5): show toast notification for incoming messages
      // (was previously only updating the bell counter)
      const fromName = data?.sender_name || 'Someone';
      const subject = data?.subject || '';
      const preview = (data?.content || '').slice(0, 80);
      const exerciseName = data?.exercise_name ? ` · ${data.exercise_name}` : '';
      toast.message(`New message from ${fromName}${exerciseName}`, {
        description: subject ? `${subject}: ${preview}` : preview,
        duration: 6000,
      });
    });

    // 6. inject_triggered — inject played by controller
    socket.on('inject_triggered', (data) => {
      const injectId = data?.inject_id;
      if (!isNewEvent(injectId)) return;

      setMessages(prev => [...prev.slice(-99), { type: 'inject_triggered', ...data }]);
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'inject_triggered', ...data } }));

      // Show modal for participants only
      const currentUserRoles = userRef.current?.roles || [];
      const isParticipant = currentUserRoles.includes('participant') &&
        !currentUserRoles.includes('super_admin') &&
        !currentUserRoles.includes('simcel') &&
        !currentUserRoles.includes('controller');

      // Participants no longer receive inject alerts — silent for them
      // (inject notifications are admin/simcell only)
    });

    // 7. inject_response — participant responded to inject
    socket.on('inject_response', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'inject_response', ...data } }));
    });

    // 7b. inject_updated — V14: status changes broadcast in real-time
    socket.on('inject_updated', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'inject_updated', ...data } }));
    });

    // V14.5 — map collaboration events (multi-user real-time map editing)
    socket.on('map_updated', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'map_updated', ...data } }));
    });
    socket.on('map_feature_added', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'map_feature_added', ...data } }));
    });
    socket.on('map_feature_deleted', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'map_feature_deleted', ...data } }));
    });

    // V14.5 — exercise updated (for global propagation of name/timezone/company changes)
    socket.on('exercise_updated', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'exercise_updated', ...data } }));
    });

    // 9. presence_update — user online/offline
    socket.on('presence_update', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'presence_update', ...data } }));
    });

    // 10. exercise_update, exercise_status_change, exercise_control
    for (const evt of ['exercise_update', 'exercise_status_change', 'exercise_control']) {
      socket.on(evt, (data) => {
        window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: evt, ...data } }));
      });
    }

    // 11. timeline_event, timeline_note
    for (const evt of ['timeline_event', 'timeline_note']) {
      socket.on(evt, (data) => {
        window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: evt, ...data } }));
      });
    }

    // 12. mp_post_published + MP events
    for (const evt of ['new_post', 'new_comment', 'new_reaction', 'post_updated', 'mp_post_published']) {
      socket.on(evt, (data) => {
        window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: evt, ...data } }));
      });
    }

    // 13. security_alert — admin only
    socket.on('security_alert', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'security_alert', ...data } }));
    });

    // 14. exercise_timer — timer sync
    socket.on('exercise_timer', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'exercise_timer', ...data } }));
    });

    // 15. map_update — live map changes
    socket.on('map_update', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'map_update', ...data } }));
    });

    // 16. user_role_change — roles changed by admin, refresh auth
    socket.on('user_role_change', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'user_role_change', ...data } }));
      if (refreshUser) refreshUser();
    });

    // 17. activity_log events
    socket.on('new_activity_log', (data) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'new_activity_log', ...data } }));
    });

    // PBX events are handled by PBXContext directly via socket
    // incoming_call, call_answer, call_hangup, ice_candidate, pbx_error, online_users

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process notification queue — show one at a time
  const processNotificationQueue = useCallback(() => {
    if (!currentNotification && notificationQueue.length > 0) {
      const [next, ...rest] = notificationQueue;
      setCurrentNotification(next);
      setNotificationQueue(rest);
      setNotificationModalOpen(true);
    }
  }, [notificationQueue, currentNotification]);

  useEffect(() => {
    const timer = setTimeout(processNotificationQueue, 0);
    return () => clearTimeout(timer);
  }, [processNotificationQueue]);

  const handleAcknowledgeNotification = useCallback(() => {
    setNotificationModalOpen(false);
    setCurrentNotification(null);
  }, []);

  const handleDismissMessagePopup = useCallback(() => {
    if (messagePopupQueue.current.length > 0) {
      setMessagePopup(messagePopupQueue.current.shift());
    } else {
      setMessagePopup(null);
    }
  }, []);

  // Public API
  const joinExercise = useCallback((exerciseId) => {
    socketRef.current?.emit('join_exercise', { exercise_id: exerciseId });
  }, []);

  const leaveExercise = useCallback((exerciseId) => {
    socketRef.current?.emit('leave_exercise', { exercise_id: exerciseId });
  }, []);

  const joinMpExercise = useCallback((exerciseId) => {
    socketRef.current?.emit('join_mp_exercise', { exercise_id: exerciseId });
  }, []);

  const leaveMpExercise = useCallback((exerciseId) => {
    socketRef.current?.emit('leave_mp_exercise', { exercise_id: exerciseId });
  }, []);

  const joinGuest = useCallback((guestToken) => {
    socketRef.current?.emit('join_guest', { guest_token: guestToken });
  }, []);

  const send = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  const getSocket = useCallback(() => socketRef.current, []);

  const value = {
    connected,
    reconnecting,
    messages,
    send,
    joinExercise,
    leaveExercise,
    joinMpExercise,
    leaveMpExercise,
    joinGuest,
    getSocket,
    notificationQueue,
    currentNotification,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}

      {/* Reconnection banner */}
      {reconnecting && (
        <div
          data-testid="reconnecting-banner"
          className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black text-center py-1.5 text-sm font-medium"
        >
          Reconnecting to server...
        </div>
      )}

      {/* Inject notification modal */}
      <InjectNotificationModal
        open={notificationModalOpen}
        onAcknowledge={handleAcknowledgeNotification}
        notification={currentNotification}
      />

      {/* Message notification popup */}
      <MessageNotificationPopup
        notification={messagePopup}
        onDismiss={handleDismissMessagePopup}
      />
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
